import React, { useCallback, useMemo, useState } from "react";
import { Button, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useStickerPrinterStatus } from "../../hooks/useStickerPrinterStatus";
import {
  useCase,
  useCaseSpecimens,
  useCaseBlocks,
  useCaseSlides,
} from "../../hooks/useCases";
import { workstationPreferenceService } from "../../services/workstationPreference.service";
import {
  getStickerTemplateUpdateMessage,
  isStickerTemplateVersionAtLeast,
  MIN_DYMO_SLIDE_TEMPLATE_VERSION,
  stickerLabelPrinterService,
  type StickerLabelData,
} from "../../services/stickerLabelPrinter.service";
import { IconSticker } from "../../theme/printColors";
import type {
  CaseBlock,
  CaseSlide,
  CaseSpecimen,
} from "../../services/cases.service";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import { requireDateOnlyString } from "../../utils/dateOnly";

interface Props {
  caseId: string;
  itemId: string;
  itemType: "slide" | "block";
  /** Slide already rendered in a parent-owned specimen/block hierarchy. */
  renderedSlide?: CaseSlide;
  /** Blocks must be received/completed; persisted slides are always eligible. */
  isCompleted?: boolean;
}

export const PrintStickerButton: React.FC<Props> = ({
  caseId,
  itemId,
  itemType,
  renderedSlide,
  isCompleted,
}) => {
  const { data: status } = useStickerPrinterStatus();
  const { data: caseData } = useCase(caseId);
  const { data: specimens } = useCaseSpecimens(caseId);
  const { data: blocks } = useCaseBlocks(caseId);
  const { data: slides } = useCaseSlides(caseId);
  const [loading, setLoading] = useState(false);
  const canPrintSticker = useHasPermission("print.sticker");

  const isConfigured = status?.configured;
  const hasCurrentSlideTemplate =
    itemType !== "slide" ||
    isStickerTemplateVersionAtLeast(
      status?.stickerTemplateVersion,
      MIN_DYMO_SLIDE_TEMPLATE_VERSION
    );
  const isReady = status?.status === "connected" && hasCurrentSlideTemplate;

  const targetItem = useMemo(() => {
    if (itemType === "slide") {
      if (renderedSlide?.id === itemId) return renderedSlide;
      return slides?.find((slide: CaseSlide) => slide.id === itemId);
    }
    return blocks?.find((b: CaseBlock) => b.id === itemId);
  }, [blocks, itemId, itemType, renderedSlide, slides]);

  const alreadyPrinted = !!targetItem?.lastStickerPrintedAt;

  const buildLabel = useCallback(
    (isReprint: boolean): StickerLabelData | null => {
      if (!caseData || !specimens || !blocks || !targetItem) return null;
      const displayCaseNumber = getDisplayCaseNumber(caseData);
      const patientName =
        resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ??
        "";
      const dateReceived = caseData.dateReceived
        ? requireDateOnlyString(caseData.dateReceived, "Date received")
        : undefined;

      if (itemType === "slide") {
        const slide = targetItem as CaseSlide;
        const block = blocks.find((b: CaseBlock) => b.id === slide.blockId);
        const specimen = specimens.find(
          (sp: CaseSpecimen) => sp.id === slide.specimenId
        );
        const specimenNum = specimen?.number ?? "";
        const blockNum = block?.number ?? "";
        const slideNum = getPhysicalSlideNumber(slide);
        return {
          itemSlotId: `${Date.now()}-s-${slide.id}`,
          itemType: "slide",
          caseId: slide.caseId,
          itemId: slide.id,
          caseNumber: displayCaseNumber,
          patientName,
          specimen: specimenNum,
          blockId: blockNum,
          slideId: slideNum,
          stain: slide.stain?.code ?? "",
          dateReceived,
          barcodeValue: `${displayCaseNumber};${specimenNum};${blockNum};${slideNum}`,
          isReprint,
        };
      }
      const block = targetItem as CaseBlock;
      const specimen = specimens.find(
        (sp: CaseSpecimen) => sp.id === block.specimenId
      );
      const specimenNum = specimen?.number ?? "";
      const blockNum = block.number ?? "";
      return {
        itemSlotId: `${Date.now()}-b-${block.id}`,
        itemType: "block",
        caseId,
        itemId: block.id,
        caseNumber: displayCaseNumber,
        patientName,
        specimen: specimenNum,
        blockId: blockNum,
        slideId: "",
        stain: "",
        dateReceived,
        barcodeValue: `${displayCaseNumber};${specimenNum};${blockNum}`,
        isReprint,
      };
    },
    [caseData, specimens, blocks, targetItem, itemType, caseId]
  );

  const dispatch = useCallback(
    async (label: StickerLabelData) => {
      const workstationId =
        workstationPreferenceService.getSelectedWorkstationId();
      if (!workstationId || !status?.deviceId) {
        notifications.show({
          title: "Print Failed",
          message: "Printer or workstation not ready.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      setLoading(true);
      try {
        const result = await stickerLabelPrinterService.printStickerLabels(
          [label],
          status.deviceId,
          workstationId,
          status.stickerTemplateVersion
        );
        if (result.success) {
          notifications.show({
            title: label.isReprint ? "Reprint Dispatched" : "Print Dispatched",
            message: `Sticker sent via ${result.method} bridge.`,
            color: "green",
            autoClose: 4000,
          });
        } else {
          notifications.show({
            title: label.isReprint ? "Reprint Failed" : "Print Failed",
            message: result.error || "Could not send sticker to printer.",
            color: "red",
            autoClose: 5000,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [status?.deviceId, status?.stickerTemplateVersion]
  );

  const handlePrint = useCallback(async () => {
    const label = buildLabel(alreadyPrinted);
    if (!label) {
      notifications.show({
        title: alreadyPrinted ? "Reprint Failed" : "Print Failed",
        message: "Case data not ready. Please retry in a moment.",
        color: "red",
        autoClose: 4000,
      });
      return;
    }
    await dispatch(label);
  }, [alreadyPrinted, buildLabel, dispatch]);

  const isEligible = itemType === "slide" || isCompleted === true;
  if (!isEligible || !isConfigured || !canPrintSticker) return null;

  const btn = (
    <Button
      variant="light"
      color="violet"
      size="xs"
      onClick={handlePrint}
      disabled={!isReady || loading}
      loading={loading}
      leftSection={<IconSticker size={14} />}
    >
      {alreadyPrinted ? "Reprint Sticker" : "Print Sticker"}
    </Button>
  );

  if (!isReady) {
    const tooltip = !hasCurrentSlideTemplate
      ? getStickerTemplateUpdateMessage(
          MIN_DYMO_SLIDE_TEMPLATE_VERSION,
          status?.stickerTemplateVersion
        )
      : "Printer is offline or in error state";
    return (
      <Tooltip label={tooltip}>
        <div style={{ display: "inline-block", cursor: "not-allowed" }}>
          {btn}
        </div>
      </Tooltip>
    );
  }

  return btn;
};
